import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, ExternalLink, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Badge, Btn, eur } from "../components/common";
import { WebhookLogPanel } from "../components/WebhookLogPanel";
import { BillingPanel } from "../components/BillingPanel";

/**
 * Pagina generica para qualquer conector API dinamico (criado via /app/plataformas).
 * Suporta as mesmas 5 tabs que CogniEtica/Privaxia: Tenants, Planos, Faturacao, Webhook, Configuracao.
 * Todos os endpoints usam o prefixo /api/connectors/{slug}/*.
 */
const TABS = [
  { key: "tenants", label: "Tenants" },
  { key: "plans", label: "Planos" },
  { key: "billing", label: "Faturacao e add-ons" },
  { key: "webhook", label: "Webhook" },
  { key: "config", label: "Configuracao" },
];

export default function DynamicConnectorPage() {
  const { slug } = useParams();
  const base = `/connectors/${slug}`;
  const [tab, setTab] = useState("tenants");
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get(`${base}/status`).then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false, platform_name: slug }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const title = status?.platform_name || slug;

  return (
    <div>
      <PageHeader overline="Portfolio - Conector API" title={title} />
      {status && !status.configured && (
        <div className="mb-6 px-4 py-3 rounded-lg bg-amber-50 text-amber-700 text-sm" data-testid={`gen-${slug}-not-configured`}>
          Integracao nao configurada. Va a "Configuracao" e defina o URL e a chave de API (ou faca-o em Plataformas).
        </div>
      )}
      <div className="flex gap-2 mb-8 border-b border-tint overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`gen-${slug}-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === t.key ? "border-blue text-blue" : "border-transparent text-muted2 hover:text-navy"
            }`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "tenants" && <TenantsTab base={base} slug={slug} configured={status?.configured} />}
      {tab === "plans" && <PlansTab base={base} slug={slug} configured={status?.configured} />}
      {tab === "billing" && <BillingPanel baseUrl={base} supportsAddons={true} />}
      {tab === "webhook" && <WebhookLogPanel origem={slug} title={`Webhooks recebidos de ${title}`} />}
      {tab === "config" && <ConfigTab base={base} slug={slug} status={status} onRefresh={() => api.get(`${base}/status`).then((r) => setStatus(r.data))} />}
    </div>
  );
}

function TenantsTab({ base, slug, configured }) {
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [syncState, setSyncState] = useState(null);

  const load = () => api.get(`${base}/tenants`).then((r) => setRows(r.data.tenants || [])).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => {
    if (!configured) return;
    load();
    api.get(`${base}/plans`).then((r) => setPlans(r.data.plans || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configured, base]);

  const syncSubs = async () => {
    try {
      const { data } = await api.post(`${base}/sync-subscriptions`);
      toast.success(`${data.synced} subscricoes sincronizadas`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const act = async (fn, label) => {
    try { await fn(); toast.success(label); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cols = [
    { key: "name", label: "Cliente", render: (r) => <span className="font-semibold text-navy">{r.name}</span> },
    { key: "slug", label: "Slug", render: (r) => <code className="text-xs bg-tint px-2 py-0.5 rounded">{r.slug}</code> },
    { key: "plan", label: "Plano", render: (r) => <Badge value={r.plan_key || r.plan || "-"} /> },
    { key: "portal_url", label: "Portal", render: (r) => {
      const url = r.portal_url || r.admin_login_url || r.public_report_url;
      return url ? <a href={url} target="_blank" rel="noreferrer" className="text-blue text-xs inline-flex items-center gap-1">Abrir <ExternalLink size={11} /></a> : "-";
    }},
    { key: "is_active", label: "Estado", render: (r) => <Badge value={r.is_active ? "ativa" : "suspensa"} /> },
    { key: "acoes", label: "Acoes", render: (r) => (
      <div className="flex gap-1 flex-wrap">
        {r.is_active ? (
          <button onClick={() => act(() => api.delete(`${base}/tenants/${r.slug}`), "Tenant suspenso")} data-testid={`gen-${slug}-suspend-${r.slug}`}
            className="text-xs px-2 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100">Suspender</button>
        ) : (
          <button onClick={() => act(() => api.post(`${base}/tenants/${r.slug}/reactivate`), "Tenant reativado")} data-testid={`gen-${slug}-reactivate-${r.slug}`}
            className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Reativar</button>
        )}
      </div>
    )},
  ];

  if (!configured) return <div className="text-sm text-muted2">Configure primeiro as credenciais em "Configuracao".</div>;
  return (
    <div>
      <div className="flex items-center justify-end mb-4 gap-2 flex-wrap">
        <Btn variant="ghost" onClick={syncSubs} testid={`gen-${slug}-sync`}><RefreshCw size={16} /> Sincronizar</Btn>
        <Btn variant="ghost" onClick={load} testid={`gen-${slug}-refresh`}><RefreshCw size={16} /> Atualizar</Btn>
        <Btn onClick={() => setOpen(true)} testid={`gen-${slug}-new-tenant`}><Plus size={16} /> Novo tenant</Btn>
      </div>
      <Table columns={cols} rows={rows} testid={`gen-${slug}-tenants-table`} />
      {open && <NewTenantModal base={base} slug={slug} plans={plans} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function NewTenantModal({ base, slug, plans, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", slug: "", plan_key: plans[0]?.key || "", admin_name: "", admin_email: "", admin_password: "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const passwordOk = (form.admin_password || "").length >= 8;
  const can = form.name && form.slug && form.plan_key && form.admin_name && form.admin_email && passwordOk;

  const save = async () => {
    if (!can) return toast.error("Preencha todos os campos (password min 8)");
    setSaving(true);
    try { await api.post(`${base}/tenants`, form); toast.success("Tenant criado"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid={`gen-${slug}-tenant-modal`}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-navy text-lg font-bold tracking-tight">Novo tenant</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>
        <label className="block text-sm font-semibold text-navy mb-1">Nome</label>
        <input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid={`gen-${slug}-t-name`} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <label className="block text-sm font-semibold text-navy mb-1">Slug</label>
        <input value={form.slug} onChange={(e) => upd("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} data-testid={`gen-${slug}-t-slug`} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <label className="block text-sm font-semibold text-navy mb-1">Plano</label>
        <select value={form.plan_key} onChange={(e) => upd("plan_key", e.target.value)} data-testid={`gen-${slug}-t-plan`} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg">
          {plans.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
        </select>
        <label className="block text-sm font-semibold text-navy mb-1">Nome admin</label>
        <input value={form.admin_name} onChange={(e) => upd("admin_name", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <label className="block text-sm font-semibold text-navy mb-1">Email admin</label>
        <input value={form.admin_email} onChange={(e) => upd("admin_email", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <label className="block text-sm font-semibold text-navy mb-1">Password admin (min 8)</label>
        <input type="password" value={form.admin_password} onChange={(e) => upd("admin_password", e.target.value)} className="w-full mb-4 px-3 py-2 border border-tint rounded-lg" />
        <Btn onClick={save} disabled={!can || saving} testid={`gen-${slug}-t-save`}>{saving ? "A criar..." : "Criar"}</Btn>
      </div>
    </div>
  );
}

function PlansTab({ base, slug, configured }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(`${base}/plans`).then((r) => setRows(r.data.plans || [])).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { if (configured) load(); /* eslint-disable-next-line */ }, [configured, base]);
  if (!configured) return <div className="text-sm text-muted2">Configure primeiro as credenciais.</div>;
  const cols = [
    { key: "name", label: "Plano", render: (r) => <span className="font-semibold text-navy">{r.name}</span> },
    { key: "key", label: "Chave", render: (r) => <code className="text-xs bg-tint px-2 py-0.5 rounded">{r.key}</code> },
    { key: "price_monthly", label: "Preco/mes", render: (r) => eur(r.price_monthly) },
  ];
  return (
    <div>
      <div className="flex justify-end mb-3"><Btn variant="ghost" onClick={load}><RefreshCw size={16} /> Atualizar</Btn></div>
      <Table columns={cols} rows={rows} testid={`gen-${slug}-plans-table`} />
    </div>
  );
}

function ConfigTab({ base, slug, status, onRefresh }) {
  const [settings, setSettings] = useState(null);
  const [apiUrl, setApiUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [manualSecret, setManualSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => api.get(`${base}/settings`).then((r) => { setSettings(r.data); setApiUrl(r.data.api_url || ""); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [base]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { api_url: apiUrl };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      if (manualSecret.trim()) body.webhook_secret = manualSecret.trim();
      await api.put(`${base}/settings`, body);
      setApiKey(""); setManualSecret("");
      toast.success("Credenciais guardadas (cifradas)");
      load(); onRefresh?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`${base}/settings/test`);
      toast.success(`Ligacao OK - ${data.health?.platform || data.config?.platform_name || slug}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="border border-tint rounded-lg p-6 bg-white">
        <h3 className="text-navy font-bold text-lg tracking-tight mb-1">Ligacao a {status?.platform_name || slug}</h3>
        <p className="text-muted2 text-sm mb-4">A chave e usada apenas pelo backend e guardada cifrada em base de dados.</p>
        <label className="block text-sm font-semibold text-navy mb-1">URL da API</label>
        <input value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} data-testid={`gen-${slug}-api-url`} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <label className="block text-sm font-semibold text-navy mb-1">Chave de API (X-API-Key)</label>
        <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} data-testid={`gen-${slug}-api-key`} className="w-full mb-1 px-3 py-2 border border-tint rounded-lg font-mono"
          placeholder={settings?.api_key_set ? "Definida - deixe vazio para manter" : "Cole a nova chave"} />
        <div className="text-xs text-muted2 mb-4">
          {settings?.api_key_set ? `Atual: ${settings.api_key_masked}` : "Nenhuma chave definida."}
        </div>
        <label className="block text-sm font-semibold text-navy mb-1">Webhook Signing Secret (opcional)</label>
        <input type="password" value={manualSecret} onChange={(e) => setManualSecret(e.target.value)} data-testid={`gen-${slug}-webhook-secret`} className="w-full mb-4 px-3 py-2 border border-tint rounded-lg font-mono"
          placeholder={settings?.webhook_secret_set ? `Guardado: ${settings.webhook_secret_masked}` : "Cole o secret se ja tem"} />
        <div className="flex gap-2 flex-wrap">
          <Btn onClick={save} disabled={saving} testid={`gen-${slug}-save`}>{saving ? "..." : "Guardar credenciais"}</Btn>
          <Btn variant="ghost" onClick={test} disabled={testing} testid={`gen-${slug}-test`}>
            <RefreshCw size={14} className={testing ? "animate-spin" : ""} /> {testing ? "A testar..." : "Testar ligacao"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
